<template>
  <button
    ref="reviewContainer"
    :aria-label
    class="relative h-full w-full overflow-hidden rounded-lg text-start"
    style="--review-details-padding: 8px; --review-details-gradient-top: 24px"
    type="button"
    @click="onReviewClick"
  >
    <RevIllustration
      alt=""
      class="h-full w-full object-cover"
      :height="328"
      :src="review.pictures?.[0] || ''"
      :width="328"
    />

    <RevTag
      class="absolute left-8 top-8 z-[2]"
      :label="reviewDate"
      variant="secondary"
    />

    <div
      class="mood-inverse absolute bottom-[var(--review-details-padding)] left-[var(--review-details-padding)] right-[var(--review-details-padding)] z-[2] isolate"
      :class="
        tw`
          before:content-[''] before:absolute before:-inset-[var(--review-details-padding)] before:-top-[calc(var(--review-details-gradient-top)*.7)] before:-z-10
          before:pointer-events-none before:backdrop-blur-[var(--review-details-padding)] before:rounded-lg
          before:bg-[linear-gradient(180deg,transparent_0%,rgba(15,17,23,0.56)_30%,rgba(15,17,23,0.6)_100%)]
          before:[mask:linear-gradient(180deg,transparent_0%,#000_30%,#000)]
        `
      "
    >
      <div class="flex flex-col items-start justify-between gap-4">
        <RevRating :score="review.averageRate" size="extra-small" />
        <span
          class="caption-bold text-action-default-hi flex items-center gap-4 truncate line-clamp-1 max-w-full"
        >
          <span class="truncate">
            {{ authorFullName }}
          </span>
          <IconOfficialFilled class="shrink-0" size="16" />
        </span>
      </div>
    </div>
  </button>
  <ClientOnly>
    <UserCommentModal
      :modal-name="`${GALLERY_REVIEW_ITEM_MODAL_NAME}_${review.id}`"
      :pictures="review.pictures || []"
      :review
    />
  </ClientOnly>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue'

import UserCommentModal from '@backmarket/nuxt-layer-reviews/review-display/ReviewModal.vue'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { RevIllustration } from '@ds/components/Illustration'
import { openModal } from '@ds/components/ModalBase'
import { RevRating } from '@ds/components/Rating'
import { RevTag } from '@ds/components/Tag'
import { IconOfficialFilled } from '@ds/icons/IconOfficialFilled'
import { tw } from '@ds/utils/tailwind'
import { useIntersectionObserver } from '@vueuse/core'

import type { ReviewWithCount } from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.types'
import { GALLERY_REVIEW_ITEM_MODAL_NAME } from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryReviewItem/GalleryReviewItem.constants'

const { review } = defineProps<{
  review: ReviewWithCount
  ariaLabel?: string
}>()

const emit = defineEmits<{
  (e: 'click'): void
  (e: 'visible'): void
}>()

const currentLocale = useI18nLocale()

const reviewDateValue = new Date(review.reviewDate)
const now = new Date()
const monthsAgo = Math.max(
  0,
  (now.getFullYear() - reviewDateValue.getFullYear()) * 12 +
    (now.getMonth() - reviewDateValue.getMonth()),
)
const reviewDate = new Intl.RelativeTimeFormat(currentLocale || 'en', {
  numeric: 'always',
}).format(-monthsAgo, 'month')

const authorFullName = [review.customer?.firstName, review.customer?.lastName]
  .filter(Boolean)
  .join(' ')

function onReviewClick() {
  emit('click')
  openModal(`${GALLERY_REVIEW_ITEM_MODAL_NAME}_${review.id}`)
}

const reviewContainer = useTemplateRef('reviewContainer')
const { stop } = useIntersectionObserver(
  reviewContainer,
  ([entry]) => {
    if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
      emit('visible')
      stop()
    }
  },
  {
    threshold: 0.7,
  },
)
</script>
